import React from 'react';
import { Link, SxProps, Theme, useTheme, Box } from '@mui/material';

interface CustomLinkProps {
    children: React.ReactNode;
    onClick?: () => void;
    variant?: 'primary' | 'secondary' | 'muted';
    size?: 'small' | 'medium' | 'large';
    underline?: 'none' | 'hover' | 'always';
    startIcon?: React.ReactNode;
    endIcon?: React.ReactNode;
    sx?: SxProps<Theme>;
}

export const CustomLink: React.FC<CustomLinkProps> = ({
    children,
    onClick,
    variant = 'primary',
    size = 'medium',
    underline = 'hover',
    startIcon,
    endIcon,
    sx = {},
}) => {
    const theme = useTheme();

    const getLinkStyles = () => {
        const baseStyles = {
            textDecoration: 'none',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            display: 'inline-block',
        };

        const variantStyles = {
            primary: {
                color: theme.palette.text.primary,
                fontSize: '1rem',
                fontWeight: 400,
                '&:hover': {
                    color: theme.palette.primary.main,
                    textDecoration: 'none'
                }
            },
            secondary: {
                color: theme.palette.text.secondary,
                '&:hover': {
                    color: theme.palette.text.primary,
                    textDecoration: 'none'
                }
            },
            muted: {
                color: theme.palette.text.secondary,
                opacity: 0.7,
                '&:hover': {
                    color: theme.palette.text.secondary,
                    opacity: 1,
                    textDecoration: 'none'
                }
            },
        };

        const sizeStyles = {
            small: {
                fontSize: '0.9rem',
            },
            medium: {
                fontSize: '1rem',
            },
            large: {
                fontSize: '1.1rem',
            },
        };

        return {
            ...baseStyles,
            ...variantStyles[variant],
            ...sizeStyles[size],
        };
    };

    return (
        <Link
            component="button"
            type="button"
            variant="body2"
            onClick={onClick}
            style={{
                ...getLinkStyles(),
                textDecoration: underline === 'always' ? 'underline' : 'none',
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {startIcon && <Box sx={{ display: 'flex', alignItems: 'center' }}>{startIcon}</Box>}
                {children}
                {endIcon && <Box sx={{ display: 'flex', alignItems: 'center' }}>{endIcon}</Box>}
            </Box>
        </Link>
    );
};
